import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ProgressCircle } from '@/domain/vertraege/features/VertragCarousel/card/ProgressCircle';
import {
  vertragUpgradeFormSchema,
  FormSchema,
} from '@/domain/vertraege/features/VertragUpgraden/schema';
import { useVertraegeUpgraden } from '@/domain/vertraege/shared/hooks/useVertraegeUpgraden';
import { Vertrag, VertragTarif } from '@/domain/vertraege/shared/types/vertrag';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line max-lines-per-function
export function VertragUpgraden({
  vertrag,
  anzahlMitarbeiter,
  kontingent,
  preis,
}: {
  vertrag: Vertrag;
  anzahlMitarbeiter: number;
  kontingent: number;
  preis: string;
}) {
  const { t } = useTranslation();
  const { manager } = useProfile();
  const [open, setOpen] = useState(false);
  const [upgraden, { loading }] = useVertraegeUpgraden();

  const form = useForm<FormSchema>({
    resolver: zodResolver(vertragUpgradeFormSchema),
    defaultValues: {
      telefon: '',
      beschreibung: '',
    },
  });

  function onSubmit(values: FormSchema) {
    upgraden({
      input: {
        telefon: values.telefon,
        datum: dayjs().toISOString(),
        email: manager.email,
        freitext: values.beschreibung,
        id: 'W-' + vertrag.id,
        kundennummer: 'K-' + vertrag.kundennummer,
        mitarbeiterAnzahl: anzahlMitarbeiter,
        vorname: manager.vorname,
        nachname: manager.nachname,
        unternehmen: vertrag.unternehmensname,
      },
    }).then(() => {
      toast({
        title: t('domain.vertraege.upgradeForm.submit.successTitel'),
        description: t(
          'domain.vertraege.upgradeForm.submit.successDescription',
        ),
      });
      setOpen(false);
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          disabled={anzahlMitarbeiter <= vertrag.aktiveAnzahl}>
          {t('domain.vertraege.upgradeInfo.actions.bestaetigen')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('domain.vertraege.upgradeForm.titel')}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.name.label')}
          </div>
          <div className="font-semibold">{`${manager.vorname} ${manager.nachname}`}</div>
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.vertragsnummer.label')}
          </div>
          <div className="font-semibold">W-{vertrag.id}</div>
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.kundenummer.label')}
          </div>
          <div className="font-semibold">K-{vertrag.kundennummer}</div>
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.kontingent.label')}
          </div>
          <div className="flex gap-3 items-center">
            <ProgressCircle
              vertrag={vertrag}
              percentage={Math.round((vertrag.aktiveAnzahl / kontingent) * 100)}
            />
            <div className="w-2/3">
              <div className="w-full font-medium">
                <span className="text-neutrals-N800 dark:text-white text-xl">
                  {vertrag.aktiveAnzahl}
                </span>
                <span className="text-neutrals-N700 dark:text-white"> / </span>
                <span className="text-neutrals-N700 text-xs">{kontingent}</span>
              </div>
              <div className="text-xs">
                {vertrag.vertragTarif === VertragTarif.MITARBEITER
                  ? t('domain.vertraege.slide.employeeTitle')
                  : t('domain.vertraege.slide.locationTitle')}
              </div>
            </div>
          </div>
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.betrag.label')}
          </div>
          <div className="font-semibold">{preis}</div>
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.abrechnungsBeginn.label')}
          </div>
          <div className="flex flex-col">
            <div className="font-semibold">
              {dayjs(vertrag.faelligkeitsdatum).format('DD.MM.YYYY')}
            </div>
            <div className="text-xs text-neutrals-N700">
              {t('domain.vertraege.upgradeForm.abrechnungsBeginn.info')}
            </div>
          </div>
          <div className="text-neutrals-N700">
            {t('domain.vertraege.upgradeForm.intervall.label')}
          </div>
          <div className="flex flex-col">
            <div className="font-semibold">
              <div className="text-sm font-bold">
                {vertrag.vertragIntervall === 'JAHR'
                  ? t('domain.vertraege.slide.yearly')
                  : t('domain.vertraege.slide.monthly')}
              </div>
            </div>
            {vertrag.vertragIntervall === 'JAHR' && (
              <div className="text-xs text-neutrals-N700">
                {t('domain.vertraege.upgradeForm.intervall.info')}
              </div>
            )}
          </div>
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 mt-4">
            <div className="flex gap-4 w-full">
              <FormField
                control={form.control}
                name="telefon"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t('domain.vertraege.kuendigenForm.fields.telefon.label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Telefonnummer eingeben"
                        type="text"
                        id="telefon"
                        autoComplete="off"
                        data-1p-ignore
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="beschreibung"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      'domain.vertraege.kuendigenForm.fields.beschreibung.label',
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      id="beschreibung"
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end mt-4">
              <div className="flex justify-end mt-4">
                <Button type="submit" disabled={loading}>
                  {t('domain.vertraege.upgradeForm.submit.label')}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
